import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateKycFormTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfo1Id = uuidv4();
  const contactInfo1BlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const dateId = uuidv4();
  const dateBlockId = uuidv4();
  const contactInfo2Id = uuidv4();
  const contactInfo2BlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const contactInfo1MediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  return {
    ...themes.forestMintFresh,
    "collectionId": collectionId,
    "name": "Know Your Client (KYC) Form",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO-black.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Let's verify your identity",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Once we confirm your details, you'll be ready to access your account.",
        "thirdContent": "I'm ready",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "horizontalAlignment": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "black Android smartphone",
          "link": "https://unsplash.com/photos/sScmok4Iq1o/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyfHxtb2JpbGUlMjBhcHB8ZW58MHx8fHwxNzY0NDA3MzcyfDA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": contactInfo1Id,
        "blockId": contactInfo1BlockId,
        "content": "What's your full name?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll use this to match your profile.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": true
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "horizontalAlignment": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UPLOADED",
          "id": contactInfo1MediaId,
          "type": "image",
          "name": "typeform-import-d99e3fbe-d104-4299-aed4-10ba5ac6af32.png",
          "link": "https://s3.amazonaws.com/leadrr-prod-uploads/typeform-imports/0d5b697e-e386-46ef-a2c7-34044737a773/typeform-import-d99e3fbe-d104-4299-aed4-10ba5ac6af32.png",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": emailId,
        "blockId": emailBlockId,
        "content": `Thanks, {{${contactInfo1BlockId}__first_name}}. What's the best email to reach you at?`,
        "manditory": true,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll use it to complete your setup and send important updates.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "horizontalAlignment": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "DATE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": dateId,
        "blockId": dateBlockId,
        "content": "When's your date of birth?",
        "manditory": true,
        "order": 3,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "MMDDYYYY",
        "thirdContent": "/",
        "forthContent": "This helps us confirm your identity.",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "horizontalAlignment": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfo2Id,
        "blockId": contactInfo2BlockId,
        "content": "What's your phone number?",
        "manditory": true,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We may contact you if an extra verification step is needed.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": false
          },
          "last_name": {
            "required": false,
            "visible": false
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": true
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "horizontalAlignment": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": `Thanks, {{${contactInfo1BlockId}__first_name}}. We're all set!`,
        "manditory": false,
        "order": 5,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We've got everything we need.",
        "thirdContent": "All done",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 32,
        "media": {
          "provider": "PEXEL",
          "id": endScreenMediaId,
          "type": "video",
          "name": "3625770",
          "link": "https://videos.pexels.com/video-files/3625770/3625770-uhd_1440_2732_25fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/3625770/pictures/preview-0.jpeg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      }
    ],
    "snippets": [],
    "metaTitle": "Know Your Client (KYC) Form",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateKycFormTemplate;